import { useMemo, useState } from "react";
import type { ChartTypeSelectRootArgs } from "../types";

type ExportUrlArgs = {
  ids: string;
  dateFrom: string;
  dateTo: string;
  yFrom: number;
  yTO: number;
  filter: string[];
  connectionId?: string;
};

type LegacyWindow = Window & {
  linksoft?: {
    url?: {
      exportProfilesWithFilter?: (args: ExportUrlArgs) => string;
    };
  };
  $?: {
    connection?: {
      hub?: {
        id?: string;
      };
    };
  };
};

const legacyWindow = window as LegacyWindow;

function parseInteger(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function ChartTypeSelect(props: ChartTypeSelectRootArgs) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [yFrom, setYFrom] = useState("");
  const [yTo, setYTo] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedCharts = useMemo(
    () => props.charts.filter((chart) => selectedIds.includes(chart.uniqueName)),
    [props.charts, selectedIds]
  );

  const visibleScaleChart = useMemo(() => {
    if (selectedCharts.length === 0) {
      return null;
    }

    const yUnits = Array.from(new Set(selectedCharts.map((chart) => chart.yParameterUnit ?? "")));
    return yUnits.length === 1 ? selectedCharts[0] : null;
  }, [selectedCharts]);

  function updateSelected(options: HTMLCollectionOf<HTMLOptionElement>) {
    const nextSelectedIds = Array.from(options)
      .filter((option) => option.selected)
      .map((option) => option.value);

    setSelectedIds(nextSelectedIds);

    const nextSelectedCharts = props.charts.filter((chart) => nextSelectedIds.includes(chart.uniqueName));
    const yUnits = Array.from(new Set(nextSelectedCharts.map((chart) => chart.yParameterUnit ?? "")));
    if (nextSelectedCharts.length > 0 && yUnits.length === 1) {
      setDateFrom(nextSelectedCharts[0].dateFrom);
      setDateTo(nextSelectedCharts[0].dateTo);
    }
  }

  async function exportProfiles() {
    if (selectedCharts.length === 0 || isExporting) {
      return;
    }

    const createUrl = legacyWindow.linksoft?.url?.exportProfilesWithFilter;
    if (!createUrl) {
      setErrorMessage(props.labels.exportFailed);
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);

    try {
      const url = createUrl({
        ids: props.ids,
        dateFrom,
        dateTo,
        yFrom: visibleScaleChart ? parseInteger(yFrom) : 0,
        yTO: visibleScaleChart ? parseInteger(yTo) : 0,
        filter: selectedCharts.map((chart) => chart.englishName),
        connectionId: legacyWindow.$?.connection?.hub?.id
      });

      const response = await fetch(url, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : props.labels.exportFailed);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="base-form chart-type-select-app">
      {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}

      <div className="form-group">
        <label htmlFor="chart-type-select-list">{props.labels.name}</label>
        <select
          id="chart-type-select-list"
          multiple
          name="chart-type-select"
          className="form-control"
          value={selectedIds}
          onChange={(event) => updateSelected(event.currentTarget.selectedOptions)}
        >
          {props.charts.map((chart) => (
            <option key={chart.uniqueName} value={chart.uniqueName}>
              {chart.name}
            </option>
          ))}
        </select>
      </div>

      {visibleScaleChart ? (
        <div className="form-group">
          <div className="form-group">
            <label htmlFor="chart-type-date-from">{props.labels.scaleFromAxisX}</label>
            <input
              id="chart-type-date-from"
              className="form-control"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.currentTarget.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="chart-type-date-to">{props.labels.scaleToAxisX}</label>
            <input
              id="chart-type-date-to"
              className="form-control"
              value={dateTo}
              onChange={(event) => setDateTo(event.currentTarget.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="chart-type-y-from">
              {props.labels.scaleFromAxisY} [{visibleScaleChart.yParameterUnit}]
            </label>
            <input
              id="chart-type-y-from"
              className="form-control"
              value={yFrom}
              onChange={(event) => setYFrom(event.currentTarget.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="chart-type-y-to">
              {props.labels.scaleToAxisY} [{visibleScaleChart.yParameterUnit}]
            </label>
            <input
              id="chart-type-y-to"
              className="form-control"
              value={yTo}
              onChange={(event) => setYTo(event.currentTarget.value)}
            />
          </div>
        </div>
      ) : null}

      <div className="panel-footer">
        <button
          type="button"
          className="btn btn-success btn-sm"
          disabled={selectedCharts.length === 0 || isExporting}
          onClick={() => void exportProfiles()}
        >
          <i className="fa fa-save"></i>
          {isExporting ? `${props.labels.exportProfileList}...` : props.labels.exportProfileList}
        </button>
      </div>
    </div>
  );
}
